import React, { useState, useEffect, useRef } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { getDocumentImage } from '../api/api';
import { ZoomInIcon, ZoomOutIcon } from '@react-pdf-viewer/zoom';

/**
 * ZoomModal – hi-res zoom + click-and-drag pan
 */
const ZoomModal = ({ show, page, onHide }) => {
    const [src, setSrc] = useState(null);
    const [loading, setLoading] = useState(false);
    const [scale, setScale] = useState(1);

    const defaultScaleRef = useRef(1);
    const STEP = 0.10, MIN = 0.25, MAX = 5;

    const wrapRef = useRef(null);
    const imgRef = useRef(null);
    const dragging = useRef(false);
    const startPos = useRef({ x: 0, y: 0, sl: 0, st: 0 });

    useEffect(() => {
        if (!show || !page) return;
        let revoke;

        const loadImage = async () => {
            const { docId, pageIndex, base64, isNewPage, isDuplicated, originalPageIndex, isUploaded, originalDocumentPageIndex } = page;

            if (isDuplicated && docId && originalPageIndex !== null && !isUploaded && docId !== "upload") {
                try {
                    setLoading(true);
                    const res = await getDocumentImage(docId, originalPageIndex + 1);
                    const blob = await res.blob();
                    const url = URL.createObjectURL(blob);
                    revoke = () => URL.revokeObjectURL(url);
                    setSrc(url);
                } catch {
                    setSrc(base64);
                } finally {
                    setLoading(false);
                }
                return;
            }

            if ((docId === 'upload' || isNewPage)) {
                setSrc(base64);
                return;
            }

            try {
                setLoading(true);
                const res = await getDocumentImage(docId, originalDocumentPageIndex + 1);
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                revoke = () => URL.revokeObjectURL(url);
                setSrc(url);
            } catch {
                setSrc(base64);
            } finally {
                setLoading(false);
            }
        };

        loadImage();
        return () => revoke && revoke();
    }, [show, page]);


    useEffect(() => {
        if (!show || !src) return;
        const adjustScale = () => {
            const wrap = wrapRef.current;
            const img = imgRef.current;
            if (!wrap || !img) return;

            const containerWidth = wrap.clientWidth;
            const naturalWidth = img.naturalWidth;

            if (naturalWidth > 0) {
                const fitScale = containerWidth / naturalWidth;
                defaultScaleRef.current = Math.max(MIN, Math.min(fitScale, MAX));
                setScale(defaultScaleRef.current);
            }
        };

        const timeout = setTimeout(adjustScale, 50);
        return () => clearTimeout(timeout);
    }, [show, src]);

    const zoomIn = () => setScale(s => Math.min(MAX, +(s + STEP).toFixed(2)));
    const zoomOut = () => setScale(s => Math.max(MIN, +(s - STEP).toFixed(2)));

    useEffect(() => {
        if (!show) return;

        const onKeyDown = e => {
            if (e.ctrlKey && e.key === '+') { e.preventDefault(); zoomIn(); }
            if (e.ctrlKey && e.key === '-') { e.preventDefault(); zoomOut(); }
        };

        const onWheel = e => {
            if (!e.ctrlKey || !wrapRef.current?.contains(e.target)) return;
            e.preventDefault();
            e.deltaY < 0 ? zoomIn() : zoomOut();
        };

        window.addEventListener('keydown', onKeyDown, { passive: false });
        window.addEventListener('wheel', onWheel, { passive: false });
        return () => {
            window.removeEventListener('keydown', onKeyDown);
            window.removeEventListener('wheel', onWheel);
        };
    }, [show]);

    const beginDrag = e => {
        const wrap = wrapRef.current;
        if (!wrap) return;
        dragging.current = true;
        startPos.current = {
            x: e.clientX,
            y: e.clientY,
            sl: wrap.scrollLeft,
            st: wrap.scrollTop,
        };
        wrap.style.cursor = 'grabbing';
        e.preventDefault();
    };

    const duringDrag = e => {
        if (!dragging.current) return;
        const wrap = wrapRef.current;
        const { x, y, sl, st } = startPos.current;
        wrap.scrollLeft = sl - (e.clientX - x);
        wrap.scrollTop = st - (e.clientY - y);
    };

    const endDrag = () => {
        dragging.current = false;
        if (wrapRef.current) wrapRef.current.style.cursor = 'grab';
    };

    return (
        <Modal
            className='zoom-popup'
            show={show}
            animation={false}
            size='lg'
            onHide={onHide}
            centered
        >
            <Modal.Header className='d-block justify-content-start'>
                <Modal.Title className='d-flex align-items-center justify-content-between w-100'>
                    <div>
                        <i className="fa-solid fa-magnifying-glass-plus" /> Zoom preview
                    </div>
                    <i
                        title='Close'
                        className='fa-light fa-xmark'
                        aria-hidden='true'
                        style={{ cursor: 'pointer', fontSize: 20 }}
                        onClick={onHide}
                    />
                </Modal.Title>
            </Modal.Header>

            <Modal.Body
                ref={wrapRef}
                onMouseDown={beginDrag}
                onMouseMove={duringDrag}
                onMouseUp={endDrag}
                onMouseLeave={endDrag}
                style={{
                    maxHeight: '75vh',
                    overflow: 'auto',
                    background: '#f7f7f7',
                    padding: 0,
                    cursor: 'grab',
                }}
            >
                {loading ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ height: '75vh', minHeight: 300 }}>
                        <Spinner animation="border" />
                    </div>
                ) : src && (
                    <div style={{ minHeight: '75vh', width: 'fit-content' }}>
                        <div style={{ zoom: scale, transition: 'zoom 0.1s linear' }}>
                            <img
                                ref={imgRef}
                                src={src}
                                alt="zoom-preview"
                                style={{ userSelect: 'none', display: 'block' }}
                            />
                        </div>
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer className="d-block">
                <div className="d-flex align-items-center justify-content-center gap-2">
                    <button className="rpv-core__minimal-button" onClick={zoomOut} disabled={scale <= MIN}>
                        <ZoomOutIcon />
                    </button>
                    <span className="rpv-zoom__popover-target-scale" style={{ minWidth: 40, textAlign: 'center' }}>
                        {Math.round(scale * 100)}%
                    </span>
                    <button className="rpv-core__minimal-button" onClick={zoomIn} disabled={scale >= MAX}>
                        <ZoomInIcon />
                    </button>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default ZoomModal;
